import { clsx } from 'clsx'
import { ArrowUpToLine, Bold, Copy, Italic, MessageSquarePlus, PaintBucket, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { findAnnotation, useDoc } from '@/store/documentStore'
import { HIGHLIGHT_SWATCHES, SWATCHES, type ToolStyle, useUi } from '@/store/uiStore'
import type { Annotation, FontFamily, Tool } from '@/types'
import { Divider, IconButton } from '../ui/Button'
import { Popover } from '../ui/Popover'

const STYLED_TOOLS: Tool[] = [
  'pen',
  'marker',
  'highlight',
  'underline',
  'strikeout',
  'rect',
  'ellipse',
  'line',
  'arrow',
  'text',
  'note',
]

export function toolForAnnotation(a: Annotation): Tool {
  switch (a.type) {
    case 'ink':
      return a.marker ? 'marker' : 'pen'
    case 'textEdit':
      return 'text'
    case 'image':
      return 'image'
    default:
      return a.type
  }
}

/** Patch a selected annotation with a tool-style change, mapping style keys to annotation fields. */
function patchFor(a: Annotation, p: Partial<ToolStyle>): Partial<Annotation> {
  const out: Record<string, unknown> = {}
  if (p.color !== undefined) out.color = p.color
  if (p.opacity !== undefined) out.opacity = p.opacity
  if (p.width !== undefined) {
    if (a.type === 'ink') out.width = p.width
    if (a.type === 'rect' || a.type === 'ellipse' || a.type === 'line' || a.type === 'arrow')
      out.strokeWidth = p.width
  }
  if (p.fill !== undefined) {
    if (a.type === 'rect' || a.type === 'ellipse') out.fill = p.fill
    if (a.type === 'text') out.background = p.fill
  }
  if (a.type === 'text' || a.type === 'textEdit') {
    for (const k of ['fontSize', 'fontFamily', 'bold', 'italic'] as const)
      if (p[k] !== undefined) out[k] = p[k]
  }
  return out as Partial<Annotation>
}

function styleFromAnnotation(a: Annotation, base: ToolStyle): ToolStyle {
  const s = { ...base, color: a.color, opacity: a.opacity }
  if (a.type === 'ink') s.width = a.width
  if (a.type === 'rect' || a.type === 'ellipse') Object.assign(s, { width: a.strokeWidth, fill: a.fill })
  if (a.type === 'line' || a.type === 'arrow') s.width = a.strokeWidth
  if (a.type === 'text')
    Object.assign(s, {
      fontSize: a.fontSize,
      fontFamily: a.fontFamily,
      bold: a.bold,
      italic: a.italic,
      fill: a.background,
    })
  if (a.type === 'textEdit')
    Object.assign(s, { fontSize: a.fontSize, fontFamily: a.fontFamily, bold: a.bold, italic: a.italic })
  return s
}

/**
 * Context-aware style bar: edits the defaults of the active tool, or the selected
 * annotation(s) when something is selected.
 */
export function PropertiesBar({ compact }: { compact?: boolean }) {
  const tool = useUi((s) => s.tool)
  const selection = useUi((s) => s.selection)
  const styleFor = useUi((s) => s.styleFor)
  useUi((s) => s.styles)
  const annotations = useDoc((s) => s.annotations)
  const selected = selection
    .map((id) => findAnnotation({ pages: [], annotations }, id))
    .filter((a): a is Annotation => Boolean(a))

  const target: Tool | null = selected.length
    ? toolForAnnotation(selected[0])
    : STYLED_TOOLS.includes(tool)
      ? tool
      : null
  if (!target && !selected.length) return null

  const style = selected.length
    ? styleFromAnnotation(selected[0], styleFor(target ?? 'pen'))
    : styleFor(target!)
  const set = (p: Partial<ToolStyle>) => {
    if (target && target !== 'image') useUi.getState().setStyle(target, p)
    if (selected.length) {
      const doc = useDoc.getState()
      doc.commit((s) => {
        const ann = { ...s.annotations }
        for (const a of selected) {
          ann[a.pageId] = (ann[a.pageId] ?? []).map((x) =>
            x.id === a.id ? ({ ...x, ...patchFor(x, p), updatedAt: Date.now() } as Annotation) : x,
          )
        }
        return { ...s, annotations: ann }
      })
    }
  }

  const kind = target ?? 'image'
  const hasColor = kind !== 'image'
  const hasWidth = ['pen', 'marker', 'rect', 'ellipse', 'line', 'arrow'].includes(kind)
  const hasFill = ['rect', 'ellipse', 'text'].includes(kind) && !(selected[0]?.type === 'textEdit')
  const hasText = kind === 'text'
  const hasOpacity = kind !== 'note'
  const swatches = kind === 'highlight' || kind === 'marker' ? HIGHLIGHT_SWATCHES : SWATCHES

  return (
    <div
      role="toolbar"
      aria-label="Properties"
      className={clsx(
        'no-scrollbar flex animate-pop items-center gap-1 overflow-x-auto',
        compact
          ? 'px-2 py-1.5'
          : 'max-w-[calc(100vw-24px)] rounded-2xl border border-line bg-glass px-1.5 py-1 shadow-md backdrop-blur-xl',
      )}
    >
      {hasColor && (
        <div className="flex shrink-0 items-center gap-1 px-1">
          {swatches.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={`Color ${c}`}
              onClick={() => set({ color: c })}
              className="size-6 shrink-0 rounded-full border border-black/10 transition-transform hover:scale-110 dark:border-white/15"
              style={{
                background: c,
                outline: style.color.toLowerCase() === c ? '2px solid var(--accent)' : undefined,
                outlineOffset: 2,
              }}
            />
          ))}
          <label
            className="relative flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full border border-line"
            aria-label="Custom color"
            title="Custom color"
          >
            <span
              className="size-full rounded-full"
              style={{ background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)' }}
            />
            <input
              type="color"
              value={style.color.slice(0, 7)}
              onChange={(e) => set({ color: e.target.value })}
              className="absolute inset-0 cursor-pointer opacity-0"
            />
          </label>
        </div>
      )}

      {hasWidth && (
        <>
          <Divider />
          <Slider
            label="Thickness"
            min={kind === 'marker' ? 4 : 0.5}
            max={kind === 'marker' ? 40 : 16}
            step={0.5}
            value={style.width}
            onChange={(v) => set({ width: v })}
            preview={
              <span
                className="block rounded-full bg-fg"
                style={{
                  width: Math.min(18, Math.max(2, style.width)),
                  height: Math.min(18, Math.max(2, style.width)),
                }}
              />
            }
          />
        </>
      )}

      {hasOpacity && (
        <>
          <Divider />
          <Slider
            label="Opacity"
            min={0.1}
            max={1}
            step={0.05}
            value={style.opacity}
            onChange={(v) => set({ opacity: v })}
            format={(v) => `${Math.round(v * 100)}%`}
          />
        </>
      )}

      {hasFill && (
        <>
          <Divider />
          <Popover
            align="center"
            side={compact ? 'top' : 'bottom'}
            trigger={({ toggle, ref }) => (
              <IconButton ref={ref} label="Fill" size="sm" onClick={toggle}>
                <span className="relative">
                  <PaintBucket className="size-4" />
                  <span
                    className="-bottom-1 absolute inset-x-0 h-1 rounded-full"
                    style={{
                      background: style.fill ?? 'transparent',
                      border: style.fill ? undefined : '1px dashed var(--text-3)',
                    }}
                  />
                </span>
              </IconButton>
            )}
          >
            <div className="grid grid-cols-6 gap-1.5 p-1.5">
              <button
                type="button"
                aria-label="No fill"
                onClick={() => set({ fill: null })}
                className="relative size-7 rounded-full border border-line-strong bg-surface after:absolute after:inset-x-1 after:top-1/2 after:h-0.5 after:-rotate-45 after:bg-danger"
              />
              {[
                '#ffffff',
                '#fef3c7',
                '#dbeafe',
                '#dcfce7',
                '#fee2e2',
                '#f3e8ff',
                '#111827',
                '#facc15',
                '#2563eb',
                '#16a34a',
                '#dc2626',
              ].map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Fill ${c}`}
                  onClick={() => set({ fill: c })}
                  className="size-7 rounded-full border border-black/10"
                  style={{ background: c }}
                />
              ))}
            </div>
          </Popover>
        </>
      )}

      {hasText && (
        <>
          <Divider />
          <select
            aria-label="Font"
            value={style.fontFamily}
            onChange={(e) => set({ fontFamily: e.target.value as FontFamily })}
            className="h-8 shrink-0 rounded-lg bg-transparent px-1.5 text-[13px] outline-none hover:bg-surface-3"
          >
            <option value="sans">Sans</option>
            <option value="serif">Serif</option>
            <option value="mono">Mono</option>
          </select>
          <select
            aria-label="Font size"
            value={String(Math.round(style.fontSize))}
            onChange={(e) => set({ fontSize: Number(e.target.value) })}
            className="h-8 shrink-0 rounded-lg bg-transparent px-1.5 text-[13px] tabular-nums outline-none hover:bg-surface-3"
          >
            {[
              ...new Set([
                6,
                8,
                9,
                10,
                11,
                12,
                14,
                16,
                18,
                20,
                24,
                28,
                32,
                40,
                48,
                64,
                72,
                Math.round(style.fontSize),
              ]),
            ]
              .sort((a, b) => a - b)
              .map((n) => (
                <option key={n} value={n}>
                  {n} pt
                </option>
              ))}
          </select>
          <IconButton label="Bold" size="sm" active={style.bold} onClick={() => set({ bold: !style.bold })}>
            <Bold className="size-4" />
          </IconButton>
          <IconButton
            label="Italic"
            size="sm"
            active={style.italic}
            onClick={() => set({ italic: !style.italic })}
          >
            <Italic className="size-4" />
          </IconButton>
        </>
      )}

      {selected.length > 0 && <SelectionActions selected={selected} compact={compact} />}
    </div>
  )
}

function SelectionActions({ selected, compact }: { selected: Annotation[]; compact?: boolean }) {
  const ids = selected.map((a) => a.id)
  const [comment, setComment] = useState(selected[0]?.comment ?? '')
  useEffect(() => setComment(selected[0]?.comment ?? ''), [selected[0]?.id, selected[0]?.comment])
  return (
    <>
      <Divider />
      {selected.length === 1 && selected[0].type !== 'note' && (
        <Popover
          align="end"
          side={compact ? 'top' : 'bottom'}
          trigger={({ toggle, ref }) => (
            <IconButton
              ref={ref}
              label={selected[0].comment ? 'Edit comment' : 'Add comment'}
              size="sm"
              active={Boolean(selected[0].comment)}
              onClick={toggle}
            >
              <MessageSquarePlus className="size-4" />
            </IconButton>
          )}
        >
          {(close) => (
            <form
              className="flex w-72 flex-col gap-2 p-1.5"
              onSubmit={(e) => {
                e.preventDefault()
                useDoc.getState().updateAnnotation(ids[0], { comment: comment.trim() || undefined })
                close()
              }}
            >
              <textarea
                autoFocus
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation()
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey))
                    (e.currentTarget.form as HTMLFormElement).requestSubmit()
                }}
                placeholder="Add a comment…"
                className="w-full resize-none rounded-xl border border-line bg-surface-2 p-2.5 text-sm outline-none focus:border-accent"
              />
              <button
                type="submit"
                className="self-end rounded-lg bg-accent px-3 py-1.5 font-medium text-on-accent text-sm"
              >
                Save
              </button>
            </form>
          )}
        </Popover>
      )}
      <IconButton
        label="Duplicate"
        shortcut="Ctrl+D"
        size="sm"
        onClick={() => useUi.getState().select(useDoc.getState().duplicateAnnotations(ids))}
      >
        <Copy className="size-4" />
      </IconButton>
      <IconButton label="Bring to front" size="sm" onClick={() => useDoc.getState().bringToFront(ids)}>
        <ArrowUpToLine className="size-4" />
      </IconButton>
      <IconButton
        label="Delete"
        shortcut="Del"
        size="sm"
        className="hover:!bg-danger/10 hover:!text-danger"
        onClick={() => {
          useDoc.getState().deleteAnnotations(ids)
          useUi.getState().select([])
        }}
      >
        <Trash2 className="size-4" />
      </IconButton>
    </>
  )
}

function Slider({
  label,
  min,
  max,
  step,
  value,
  onChange,
  format,
  preview,
}: {
  label: string
  min: number
  max: number
  step: number
  value: number
  onChange: (v: number) => void
  format?: (v: number) => string
  preview?: React.ReactNode
}) {
  const [v, setV] = useState(value)
  useEffect(() => setV(value), [value])
  return (
    <label className="flex shrink-0 items-center gap-2 px-1.5 text-fg-2 text-xs" title={label}>
      <span className="sr-only">{label}</span>
      {preview ?? <span className="w-8 text-right tabular-nums">{format ? format(v) : v}</span>}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={v}
        onChange={(e) => setV(Number(e.target.value))}
        onPointerUp={() => v !== value && onChange(v)}
        onKeyUp={() => v !== value && onChange(v)}
        onBlur={() => v !== value && onChange(v)}
        className="h-1 w-20 cursor-pointer accent-[var(--accent)]"
      />
      {preview && <span className="w-7 tabular-nums">{format ? format(v) : v}</span>}
    </label>
  )
}
